import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AutoComplete } from 'primereact/autocomplete';
import { Modal } from '../../../components/Modal/Modal';
import { Button } from '../../../components/Button/Button';
import Input from '../../../components/Input/Input';
import { StepWrapper } from '../../../components/StepWrapper/StepWrapper';
import { ProcessTree } from '../../../components/ProcessTree/ProcessTree';
import { useToast } from '../../../context/ToastContext';
import { useAuth } from '../../../context/AuthContext';
import FmeaAiagVdaApi from '../../../services/FmeaAiagVdaApi';
import OperationApi from '../../../services/OperationApi';
import { useTranslation } from 'react-i18next';
import * as FmeaStep2Types from '../../../interfaces/FmeaStep2';
import styles from './Step2.module.css';

// Ensure required constants are available in the scope to avoid ReferenceErrors
const { OperationTypeLabels, Cause6MLabels } = FmeaStep2Types;

const Step2: React.FC = () => {
  const { fmeaId: fmeaIdParam } = useParams<{ fmeaId: string }>();
  const fmeaId = fmeaIdParam ?? '';
  const navigate = useNavigate();
  const { displayToast } = useToast();
  const { userId, plantId } = useAuth();
  const { t: translate } = useTranslation('form');
  
  const translateWithFallback = useCallback((key: string, fallback: string) => {
    const value = translate(key);
    return value === key ? fallback : value;
  }, [translate]);

  const emptyStateMessage = translateWithFallback('noProcessItems', 'Nenhum item cadastrado até o momento.');
  const addItemLabel = translateWithFallback('addItem', translateWithFallback('add', 'Adicionar'));
  const exportPdfLabel = translateWithFallback('exportPdf', 'Exportar PDF');
  const renameItemGlobalImpactLabel = translateWithFallback('renameItemGlobalImpact', 'Editar o nome impactará todos os FMEA AIAG-VDA que utilizam este item.');
  const stepDescriptionTitle = translateWithFallback('step2DescriptionTitle', 'Análise de Estrutura: Itens, Etapas e Elementos');
  const stepDescriptionText = translateWithFallback('step2DescriptionText', 'Defina a estrutura hierárquica da sua análise organizando os itens, as etapas (operações de trabalho) e seus elementos associados.');

  const extractErrorMessage = useCallback((error: any) => {
    if (!error) return '';
    const candidates = [
      error?.data?.error?.message,
      error?.data?.message,
      error?.response?.data?.error?.message,
      error?.response?.data?.message,
      error?.message,
    ];
    return candidates.find(entry => typeof entry === 'string' && entry.trim().length > 0) || '';
  }, []);

  const showErrorToast = useCallback((error: any, duration = 3000) => {
    const message = extractErrorMessage(error);
    displayToast('error', translate('error'), message || translate('error'), false, duration);
  }, [displayToast, extractErrorMessage, translate]);

  // Data states
  const [tree, setTree] = useState<FmeaStep2Types.StructureAnalysisTreeDto | null>(null);
  const [opsList, setOpsList] = useState<{ label: string; value: string; rank?: number }[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [readOnly, setReadOnly] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);

  // Modal states
  const [showItemModal, setShowItemModal] = useState(false);
  const [newItemName, setNewItemName] = useState('');
  const [itemSuggestions, setItemSuggestions] = useState<string[]>([]);
  const itemSuggestionMapRef = useRef<Record<string, { id: string; name: string }>>({});
  const [selectedExistingItem, setSelectedExistingItem] = useState<{ id: string; name: string } | null>(null);
  const [isCreatingNewItem, setIsCreatingNewItem] = useState(false);
  const [isEditingItem, setIsEditingItem] = useState(false);

  const [showOpModal, setShowOpModal] = useState(false);
  const [selectedItemForOp, setSelectedItemForOp] = useState<FmeaStep2Types.ProcessItemDto | null>(null);
  const [newOpForm, setNewOpForm] = useState<{ operationId: string; type: FmeaStep2Types.OperationTypeEnum | null }>({ operationId: '', type: null });

  const [showElementModal, setShowElementModal] = useState(false);
  const [editingElementContext, setEditingElementContext] = useState<any>(null);
  const [showCreateElementModal, setShowCreateElementModal] = useState(false);
  const [elementCreationContext, setElementCreationContext] = useState<{ stepId: string; itemName: string; operationId: string } | null>(null);
  const [newElementForm, setNewElementForm] = useState<any>({ elementDescription: '', cause6m: null, elementType: null });

  // Confirmation modal states
  const [confirmModal, setConfirmModal] = useState<{ visible: boolean; title: string; message: string; onAccept: () => void } | null>(null);

  /**
   * Índice de operações para exibição na árvore.
   *
   * Prefere o que o backend já resolve por etapa (operationName/operationRank vêm no
   * /tree): é autoritativo e cobre etapas cuja operação não esteja no catálogo atual.
   * O catálogo entra como complemento, para as operações ainda não usadas.
   */
  const operationIndex = useMemo(() => {
    const index = new Map<string, { name: string; rank?: number }>();

    opsList.forEach(op => {
      index.set(op.value, { name: op.label, rank: op.rank });
    });

    tree?.items?.forEach(item => {
      item.steps?.forEach(step => {
        if (!step.operationId || !step.operationName) return;
        index.set(step.operationId, {
          name: step.operationName,
          rank: step.operationRank ?? undefined,
        });
      });
    });

    return index;
  }, [opsList, tree]);

  const getOperationLabel = useCallback(
    (id: string) => operationIndex.get(id)?.name ?? id,
    [operationIndex]
  );
  const getOperationRank = useCallback(
    (id: string) => operationIndex.get(id)?.rank,
    [operationIndex]
  );

  const operationTypeOptions = useMemo(() => 
    Object.entries(OperationTypeLabels)
      .map(([value, label]) => ({
        label: translateWithFallback(`operationTypes.${label}`, label),
        value: Number(value),
      })), [translateWithFallback]);

  const cause6MOptions = useMemo(() => 
    Object.entries(Cause6MLabels).map(([value, label]) => ({
      label: translateWithFallback(`cause6m.${label}`, label as string),
      value: Number(value),
    })), [translateWithFallback]);

  const refreshTree = useCallback(async () => {
    if (!fmeaId) return;
    try {
      const data = await FmeaAiagVdaApi.GetStep2FullTree(fmeaId);
      setTree(data as FmeaStep2Types.StructureAnalysisTreeDto);
    } catch (err) {
      showErrorToast(err);
    }
  }, [fmeaId, showErrorToast]);

  const loadData = useCallback(async () => {
    if (!fmeaId) return;
    try {
      setLoading(true);
      const treeData = await FmeaAiagVdaApi.GetStep2FullTree(fmeaId);
      setTree(treeData as FmeaStep2Types.StructureAnalysisTreeDto);
      setReadOnly(treeData.completed || false);
    } catch (err) {
      showErrorToast(err);
    } finally {
      setLoading(false);
    }
  }, [fmeaId, showErrorToast]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  /**
   * Catálogo de operações que alimenta o select do modal "Adicionar Etapa".
   *
   * A sublinha vem da metadata da árvore de propósito: é o backend que discrimina FMEA de
   * produto e de família (mesma rota de etapas serve os dois) para resolver o contexto
   * produtivo, e refazer essa discriminação aqui duplicaria a regra.
   */
  const sublineId = tree?.metadata?.sublineId ?? null;

  useEffect(() => {
    if (!sublineId) {
      setOpsList([]);
      return undefined;
    }

    let active = true;

    const loadOperations = async (): Promise<void> => {
      try {
        const response = await OperationApi.GetOperationsBySubLineId(sublineId);
        if (!active) return;
        const operations: any[] = Array.isArray(response?.data) ? response.data : [];
        setOpsList(
          operations
            .map(op => ({ label: op.name as string, value: op.id as string, rank: op.rank as number | undefined }))
            .sort((a, b) => a.label.localeCompare(b.label))
        );
      } catch (err) {
        if (active) showErrorToast(err);
      }
    };

    loadOperations();
    return () => {
      active = false;
    };
  }, [sublineId, showErrorToast]);

  const handleExportPdf = useCallback(async () => {
    if (!fmeaId) return;
    try {
      setExportingPdf(true);
      const blob = await FmeaAiagVdaApi.ExportStep2Pdf(fmeaId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `fmea-step2-${fmeaId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      showErrorToast(err);
    } finally {
      setExportingPdf(false);
    }
  }, [fmeaId, showErrorToast]);

  const resetItemModalState = useCallback(() => {
    setNewItemName('');
    setItemSuggestions([]);
    itemSuggestionMapRef.current = {};
    setSelectedExistingItem(null);
    setIsCreatingNewItem(false);
    setIsEditingItem(false);
  }, []);

  const handleItemNameInput = useCallback((value: string) => {
    const val = value || '';
    setNewItemName(val);
    if (!val.trim()) {
      setSelectedExistingItem(null);
      setIsCreatingNewItem(false);
      return;
    }
    const match = itemSuggestionMapRef.current[val.trim().toLowerCase()];
    if (match) {
      setSelectedExistingItem(match);
      setIsCreatingNewItem(false);
    } else {
      setSelectedExistingItem(null);
      setIsCreatingNewItem(true);
    }
  }, []);

  const handleExistingItemSelect = useCallback((name: string) => {
    const match = itemSuggestionMapRef.current[name.trim().toLowerCase()];
    if (match) {
      setSelectedExistingItem(match);
      setNewItemName(match.name);
      setIsCreatingNewItem(false);
    }
  }, []);

  const fetchItemSuggestions = useCallback(async (query: string) => {
    const sanitized = query?.trim();
    if (!sanitized || !plantId) return;
    try {
      const response = await FmeaAiagVdaApi.SearchProcessItemsByPlant(plantId, sanitized);
      const items = Array.isArray(response) ? response : [];
      const map: Record<string, any> = {};
      items.forEach(i => map[i.name.trim().toLowerCase()] = i);
      itemSuggestionMapRef.current = map;
      setItemSuggestions(items.map(i => i.name));
    } catch (err) {
      showErrorToast(err);
    }
  }, [plantId, showErrorToast]);

  const saveItem = async (): Promise<void> => {
    if (isEditingItem && selectedItemForOp) {
      if (!newItemName.trim() || newItemName === selectedItemForOp.name) {
        setShowItemModal(false);
        return;
      }
      try {
        setSaving(true);
        await FmeaAiagVdaApi.UpdateProcessItem(fmeaId, selectedItemForOp.id, newItemName.trim(), userId || '');
        displayToast('success', translate('success'), 'Item renomeado');
        setShowItemModal(false);
        refreshTree();
      } catch (err) {
        showErrorToast(err);
      } finally {
        setSaving(false);
      }
      return;
    }

    if (!newItemName.trim()) return;
    try {
      setSaving(true);
      await FmeaAiagVdaApi.CreateProcessItem(fmeaId, {
        name: newItemName.trim(),
        existingItemId: selectedExistingItem?.id,
        createdBy: userId || '',
        plantId: plantId || ''
      });
      displayToast('success', translate('success'), 'Item adicionado');
      setShowItemModal(false);
      refreshTree();
    } catch (err) {
      showErrorToast(err);
    } finally {
      setSaving(false);
    }
  };

  const deleteItem = (item: FmeaStep2Types.ProcessItemDto): void => {
    if (item.steps.length > 0) return;
    setConfirmModal({
      visible: true,
      title: translate('delete'),
      message: 'Remover este item?',
      onAccept: async () => {
        try {
          await FmeaAiagVdaApi.DeleteProcessItem(fmeaId, item.id);
          displayToast('success', translate('success'), 'Item removido');
          refreshTree();
          setConfirmModal(null);
        } catch (err) {
          showErrorToast(err);
        }
      }
    });
  };

  const handleAddOperation = async (): Promise<void> => {
    if (!selectedItemForOp || !newOpForm.operationId || newOpForm.type == null) return;
    try {
      setSaving(true);
      await FmeaAiagVdaApi.CreateProcessOperation({
        fmeaId,
        itemId: selectedItemForOp.id,
        operationId: newOpForm.operationId,
        type: newOpForm.type,
        createdBy: userId || ''
      });
      displayToast('success', translate('success'), 'Operação adicionada');
      setShowOpModal(false);
      setNewOpForm({ operationId: '', type: null });
      refreshTree();
    } catch (err) {
      showErrorToast(err);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteOperation = (step: FmeaStep2Types.ProcessStepDto): void => {
    setConfirmModal({
      visible: true,
      title: translate('delete'),
      message: 'Remover esta etapa?',
      onAccept: async () => {
        try {
          await FmeaAiagVdaApi.DeleteProcessOperation(fmeaId, step.id);
          displayToast('success', translate('success'), 'Etapa removida');
          refreshTree();
          setConfirmModal(null);
        } catch (err) {
          showErrorToast(err);
        }
      }
    });
  };

  const saveElement = async (): Promise<void> => {
    if (!newElementForm.elementDescription) return;
    try {
      setSaving(true);
      if (editingElementContext) {
        await FmeaAiagVdaApi.UpdateWorkElement(fmeaId, editingElementContext.elementId, {
          elementDescription: newElementForm.elementDescription,
          cause6m: newElementForm.cause6m!,
          elementType: newElementForm.elementType!,
          updatedBy: userId || ''
        });
        displayToast('success', translate('success'), 'Elemento atualizado');
      } else if (elementCreationContext) {
        await FmeaAiagVdaApi.CreateWorkElement(fmeaId, elementCreationContext.stepId, {
          elementDescription: newElementForm.elementDescription,
          cause6m: newElementForm.cause6m!,
          elementType: newElementForm.elementType!,
          createdBy: userId || ''
        });
        displayToast('success', translate('success'), 'Elemento adicionado');
      }
      setShowElementModal(false);
      setShowCreateElementModal(false);
      setNewElementForm({ elementDescription: '', cause6m: null, elementType: null });
      setEditingElementContext(null);
      refreshTree();
    } catch (err) {
      showErrorToast(err);
    } finally {
      setSaving(false);
    }
  };

  const deleteElement = (id: string): void => {
    setConfirmModal({
      visible: true,
      title: translate('delete'),
      message: 'Remover este elemento?',
      onAccept: async () => {
        try {
          await FmeaAiagVdaApi.DeleteWorkElement(fmeaId, id);
          displayToast('success', translate('success'), 'Elemento removido');
          refreshTree();
          setConfirmModal(null);
        } catch (err) {
          showErrorToast(err);
        }
      }
    });
  };

  return (
    <>
      <StepWrapper
        title={stepDescriptionTitle}
        description={stepDescriptionText}
        currentStep={2}
        onPrevious={() => navigate(`/fmeas/${fmeaId}/steps/1`)}
        onNext={() => navigate(`/fmeas/${fmeaId}/steps/3`)}
        nextDisabled={readOnly}
      >
        <ProcessTree
          items={tree?.items || []}
          readOnly={readOnly}
          loading={loading}
          getOperationLabel={getOperationLabel}
          getOperationRank={getOperationRank}
          operationTypeLabels={OperationTypeLabels}
          onAddStep={(item) => { setSelectedItemForOp(item); setShowOpModal(true); }}
          onEditItem={(item) => {
            setSelectedItemForOp(item);
            setNewItemName(item.name);
            setIsEditingItem(true);
            setShowItemModal(true);
          }}
          onAddElement={(item, step) => {
            setElementCreationContext({ stepId: step.id, itemName: item.name, operationId: step.operationId });
            setNewElementForm({ elementDescription: '', cause6m: null, elementType: null });
            setShowCreateElementModal(true);
          }}
          onEditElement={(el) => {
            setEditingElementContext({ elementId: el.id, ...el });
            setNewElementForm({ ...el });
            setShowElementModal(true);
          }}
          onDeleteElement={deleteElement}
          onDeleteStep={handleDeleteOperation}
          onDeleteItem={deleteItem}
          toolbar={
            <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
              <div>
                {!readOnly && (
                  <Button
                    label={addItemLabel}
                    icon="add"
                    disabled={(tree?.items?.length || 0) >= 1}
                    onClick={() => { resetItemModalState(); setShowItemModal(true); }}
                  />
                )}
              </div>
              <div>
                <Button
                  label={exportPdfLabel}
                  icon="picture_as_pdf"
                  variant="outlined"
                  severity="secondary"
                  loading={exportingPdf}
                  onClick={handleExportPdf}
                />
              </div>
            </div>
          }
          emptyState={
            <div className={styles.emptyState}>
              <i className="material-symbols-outlined">inventory_2</i>
              <p>{emptyStateMessage}</p>
              {!readOnly && <Button label={addItemLabel} icon="add" onClick={() => { resetItemModalState(); setShowItemModal(true); }} />}
            </div>
          }
        />
      </StepWrapper>

      <Modal
        visible={showItemModal}
        onHide={() => setShowItemModal(false)}
        header={isEditingItem ? 'Editar Item' : addItemLabel}
        footer={
          <div className="flex gap-2">
            <Button label="Cancelar" variant="outlined" severity="secondary" onClick={() => setShowItemModal(false)} />
            <Button label="Salvar" severity="success" onClick={saveItem} loading={saving} disabled={!newItemName.trim()} />
          </div>
        }
      >
        <div className={styles.modalContent}>
          {isEditingItem ? (
             <div className={styles.renameContainer}>
                <p className={styles.warningText}>{renameItemGlobalImpactLabel}</p>
                <Input type="text" label="Nome do Item" value={newItemName} onChange={setNewItemName} maxLength={80} />
             </div>
          ) : (
            <>
              <p>Busque um item existente ou crie um novo.</p>
              <div className={styles.autoCompleteWrapper}>
                <label className={styles.fieldLabel}>Nome do Item</label>
                <AutoComplete
                  value={newItemName}
                  suggestions={itemSuggestions}
                  completeMethod={e => fetchItemSuggestions(e.query)}
                  onChange={e => handleItemNameInput(e.value)}
                  onSelect={e => handleExistingItemSelect(e.value)}
                  maxLength={80}
                  className={styles.autoCompleteInput}
                />
              </div>
              {selectedExistingItem && <p className={styles.successText}>Item existente selecionado. Ele será vinculado.</p>}
              {isCreatingNewItem && <p className={styles.infoText}>Novo item será criado.</p>}
            </>
          )}
        </div>
      </Modal>

      <Modal
        visible={showOpModal}
        onHide={() => setShowOpModal(false)}
        header="Adicionar Etapa"
        footer={
          <div className="flex gap-2">
            <Button label="Cancelar" variant="outlined" severity="secondary" onClick={() => setShowOpModal(false)} />
            <Button label="Adicionar" severity="success" onClick={handleAddOperation} loading={saving} disabled={!newOpForm.operationId || newOpForm.type == null} />
          </div>
        }
      >
        <div className={styles.modalContent}>
          <div className={styles.fieldGroup}>
            <label className={styles.fieldLabel}>Operação</label>
            <select 
              value={newOpForm.operationId} 
              onChange={e => setNewOpForm(prev => ({ ...prev, operationId: e.target.value }))}
              className={styles.selectInput}
            >
              <option value="">Selecione...</option>
              {opsList.map(op => <option key={op.value} value={op.value}>{op.label}</option>)}
            </select>
          </div>
          <div className={styles.fieldGroup}>
            <label className={styles.fieldLabel}>Tipo</label>
            <select 
              value={newOpForm.type || ''} 
              onChange={e => setNewOpForm(prev => ({ ...prev, type: Number(e.target.value) }))}
              className={styles.selectInput}
            >
              <option value="">Selecione...</option>
              {operationTypeOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
            </select>
          </div>
        </div>
      </Modal>

      <Modal
        visible={showCreateElementModal || showElementModal}
        onHide={() => { setShowCreateElementModal(false); setShowElementModal(false); }}
        header={editingElementContext ? 'Editar Elemento' : 'Novo Elemento'}
        footer={
          <div className="flex gap-2">
            <Button label="Cancelar" variant="outlined" severity="secondary" onClick={() => { setShowCreateElementModal(false); setShowElementModal(false); }} />
            <Button label="Salvar" severity="success" onClick={saveElement} loading={saving} disabled={!newElementForm.elementDescription} />
          </div>
        }
      >
        <div className={styles.modalContent}>
          <Input 
            type="text"
            label="Descrição do Elemento" 
            value={newElementForm.elementDescription} 
            onChange={v => setNewElementForm((p: any) => ({ ...p, elementDescription: v }))} 
          />
          <div className={styles.fieldGroup}>
            <label className={styles.fieldLabel}>Causa 6M</label>
            <select 
              value={newElementForm.cause6m ?? ''} 
              onChange={e => setNewElementForm((p: any) => ({ ...p, cause6m: Number(e.target.value) }))}
              className={styles.selectInput}
            >
              <option value="">Selecione...</option>
              {cause6MOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
            </select>
          </div>
          <div className={styles.fieldGroup}>
            <label className={styles.fieldLabel}>Tipo de Elemento</label>
            <select 
              value={newElementForm.elementType ?? ''} 
              onChange={e => setNewElementForm((p: any) => ({ ...p, elementType: Number(e.target.value) }))}
              className={styles.selectInput}
            >
              <option value="">Selecione...</option>
              <option value={1}>Valor Agregado</option>
              <option value={2}>Não Agregado</option>
              <option value={3}>Não Agregado Necessário</option>
            </select>
          </div>
        </div>
      </Modal>

      {confirmModal && (
        <Modal
          visible={confirmModal.visible}
          onHide={() => setConfirmModal(null)}
          header={confirmModal.title}
          footer={
            <div className="flex gap-2">
              <Button label="Não" variant="outlined" severity="secondary" onClick={() => setConfirmModal(null)} />
              <Button label="Sim" severity="danger" onClick={confirmModal.onAccept} />
            </div>
          }
        >
          <div style={{ margin: 0, color: 'var(--color-neutral-700, #374151)' }}>
            {confirmModal.message}
          </div>
        </Modal>
      )}
    </>
  );
};

export default Step2;
